import { collection, query, where, getDocs, orderBy, limit, type QueryDocumentSnapshot } from 'firebase/firestore';
import { db } from '../../config';
import type { ClinicalExam, ExamStatus } from './types';
import { handleFirebaseError } from '../../utils';
import { getCurrentPeriod, canCreateNewRecord } from '@/lib/utils/periods';
import { pickLatestExam } from './examPeriod';
import { CHUNK_CONCURRENCY, chunkIds, forEachLimited } from '../batchedIn';

/** A clinicalExams doc as getCustomerExams returns it. */
function toCustomerExam(doc: QueryDocumentSnapshot): ClinicalExam {
  const data = doc.data();
  return {
    id: doc.id,
    ...data,
    date: data.date?.toDate ? data.date.toDate() : new Date(data.date)
  } as ClinicalExam;
}

const newestFirst = (a: ClinicalExam, b: ClinicalExam) => b.date.getTime() - a.date.getTime();

export async function getCustomerExams(customerId: string): Promise<ClinicalExam[]> {
  try {
    const examsQuery = query(
      collection(db, 'clinicalExams'),
      where('customerId', '==', customerId)
    );
    
    const snapshot = await getDocs(examsQuery);
    const exams = snapshot.docs.map(toCustomerExam);

    return exams.sort(newestFirst);
  } catch (error) {
    handleFirebaseError(error, 'Error fetching customer exams');
    return [];
  }
}

/**
 * getCustomerExams for a whole roster (#117): one `in` query per 30
 * customers, CHUNK_CONCURRENCY chunks at once. Every customer of a chunk that
 * loaded is in the map (no exams = []); a failed chunk is logged and its
 * customers are left out (unknown), where getCustomerExams would throw.
 */
export async function getCustomersExams(customerIds: string[]): Promise<Map<string, ClinicalExam[]>> {
  const out = new Map<string, ClinicalExam[]>();
  await forEachLimited(chunkIds(customerIds), CHUNK_CONCURRENCY, async (part) => {
    try {
      const snapshot = await getDocs(query(collection(db, 'clinicalExams'), where('customerId', 'in', part)));
      const byCustomer = new Map<string, ClinicalExam[]>(part.map((id) => [id, []]));
      snapshot.docs.forEach((doc) => byCustomer.get(doc.data().customerId)?.push(toCustomerExam(doc)));
      byCustomer.forEach((exams, id) => out.set(id, exams.sort(newestFirst)));
    } catch (error) {
      console.error('Error fetching exams for a roster chunk:', error);
    }
  });
  return out;
}

export async function checkExamEligibility(customerId: string): Promise<{
  eligible: boolean;
  lastExamDate?: Date;
  daysUntilEligible?: number;
}> {
  try {
    const examsQuery = query(
      collection(db, 'clinicalExams'),
      where('customerId', '==', customerId),
      where('status', '==', 'completed')
    );

    const snapshot = await getDocs(examsQuery);
    const exams = snapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        ...data,
        date: data.date?.toDate ? data.date.toDate() : new Date(data.date)
      };
    }) as ClinicalExam[];

    if (exams.length === 0) {
      return { eligible: true };
    }

    const sortedExams = exams.sort((a, b) => b.date.getTime() - a.date.getTime());
    const lastExamDate = sortedExams[0].date;
    const daysSinceLastExam = Math.floor(
      (Date.now() - lastExamDate.getTime()) / (1000 * 60 * 60 * 24)
    );

    // Assuming 30 days between exams
    const daysUntilEligible = Math.max(0, 30 - daysSinceLastExam);

    return { 
      eligible: daysSinceLastExam >= 30,
      lastExamDate,
      daysUntilEligible
    };
  } catch (error) {
    console.error('Error checking exam eligibility:', error);
    // Return default values in case of error
    return { eligible: true };
  }
}

export async function canTakeExam(customerId: string): Promise<{
  allowed: boolean;
  existingExam?: ClinicalExam;
}> {
  try {
    const currentPeriod = getCurrentPeriod();
    
    // Check if there's already an exam in the current period
    const currentPeriodExamsQuery = query(
      collection(db, 'clinicalExams'),
      where('customerId', '==', customerId),
      where('periodId', '==', currentPeriod.id)
    );

    const currentPeriodSnapshot = await getDocs(currentPeriodExamsQuery);

    if (currentPeriodSnapshot.docs.length > 0) {
      // There's already an exam in the current period. Customers can hold
      // several (the missing-periodId era duplicated them); always load the
      // most recently updated so the dialog is deterministic.
      const latest = pickLatestExam(
        currentPeriodSnapshot.docs.map((d) => ({ id: d.id, ...d.data() })),
      )!;
      const examData = latest;
      const existingExam = {
        ...examData,
        date: examData.date?.toDate ? examData.date.toDate() : new Date(examData.date)
      } as ClinicalExam;
      
      return {
        allowed: false, // Can't create new exam in same period
        existingExam
      };
    }

    return {
      allowed: true, // Can create new exam in current period
      existingExam: undefined
    };
  } catch (error) {
    console.error('Error in canTakeExam:', error);
    return { allowed: true };
  }
}

export async function getCurrentPeriodExam(customerId: string): Promise<ClinicalExam | null> {
  try {
    const currentPeriod = getCurrentPeriod();
    const examsQuery = query(
      collection(db, 'clinicalExams'),
      where('customerId', '==', customerId),
      where('periodId', '==', currentPeriod.id)
    );

    const snapshot = await getDocs(examsQuery);

    if (snapshot.docs.length > 0) {
      // Same rule as canTakeExam: the most recently updated exam wins.
      const data = pickLatestExam(snapshot.docs.map((d) => ({ id: d.id, ...d.data() })))!;
      return {
        ...data,
        date: data.date?.toDate ? data.date.toDate() : new Date(data.date),
        createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : new Date(),
        updatedAt: data.updatedAt?.toDate ? data.updatedAt.toDate() : new Date()
      } as ClinicalExam;
    }
    
    return null;
  } catch (error) {
    console.error('Error getting current period exam:', error);
    return null;
  }
}

export async function getExamHistory(customerId: string): Promise<ClinicalExam[]> {
  try {
    const examsQuery = query(
      collection(db, 'clinicalExams'),
      where('customerId', '==', customerId),
      orderBy('date', 'desc')
    );
    
    const snapshot = await getDocs(examsQuery);
    return snapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        ...data,
        date: data.date?.toDate ? data.date.toDate() : new Date(data.date),
        createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : new Date(),
        updatedAt: data.updatedAt?.toDate ? data.updatedAt.toDate() : new Date()
      } as ClinicalExam;
    });
  } catch (error) {
    handleFirebaseError(error, 'Error fetching exam history');
    return [];
  }
}